import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAppConfig, putAdminAppConfig } from "./appConfigApi";
import {
  CATALOG_FIELD_TYPES,
  fetchAllProjectRowsForCatalog,
  normalizeCatalogFieldType,
  scanFieldCatalogFromProjectRows,
} from "./fieldCatalogScan";
import {
  applyCommercialFieldCatalogStructure,
  inferFieldCatalogGroupId,
  resolveFieldCatalogGroupId,
  stableCommercialGroupId,
} from "./fieldCatalogCommercialGroups";
import { COMMERCIAL_DOMAIN_LIST } from "./commercialProjectSchema";
import { getProjectGridColumnDescriptors } from "../projectGridColumns";

function mkId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeSlug(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

const PB = {
  fieldsRoot: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    marginTop: "0.5rem",
  },
  groupBlock: (muted) => ({
    border: `1px solid ${muted ? "#94a3b8" : "#cbd5e1"}`,
    borderStyle: muted ? "dashed" : "solid",
    borderRadius: 10,
    background: muted ? "#f8fafc" : "#fff",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
  }),
  groupHead: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "0.75rem",
    padding: "0.65rem 1rem",
    background: "linear-gradient(180deg, #f1f5f9 0%, #e8eef5 100%)",
    borderBottom: "1px solid #cbd5e1",
  },
  groupTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" },
  groupMeta: { display: "flex", flexWrap: "wrap", gap: "0.35rem 0.75rem", fontSize: 11, color: "#475569" },
  pill: (kind) => {
    const map = {
      n: { background: "#e2e8f0", color: "#334155" },
      c: { background: "#dbeafe", color: "#1e40af" },
      e: { background: "#ede9fe", color: "#5b21b6" },
    };
    const s = map[kind] || map.n;
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: 999,
      fontWeight: 600,
      fontSize: 11,
      ...s,
    };
  },
  groupActions: { display: "flex", flexWrap: "wrap", gap: "0.35rem", alignItems: "center" },
  intro: { maxWidth: "52rem" },
};

function inferPanelScope(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("expansion")) return { appliesToCommercial: false, appliesToExpansion: true };
  if (n.includes("commercial")) return { appliesToCommercial: true, appliesToExpansion: false };
  if (n.includes("shared") || n.includes("common")) {
    return { appliesToCommercial: true, appliesToExpansion: true };
  }
  return { appliesToCommercial: true, appliesToExpansion: false };
}

function mapAcfWidgetType(acfType) {
  const t = String(acfType || "")
    .toLowerCase()
    .trim();
  const m = {
    text: "text",
    textarea: "text",
    wysiwyg: "text",
    number: "number",
    range: "number",
    email: "text",
    url: "url",
    password: "text",
    date_picker: "date",
    time_picker: "date",
    date_time_picker: "date",
    true_false: "boolean",
    select: "selection",
    checkbox: "selection",
    radio: "selection",
    button_group: "selection",
    file: "file",
    image: "file",
    gallery: "file",
    oembed: "url",
    link: "url",
    post_object: "json",
    relationship: "json",
    repeater: "json",
    flexible_content: "json",
    group: "json",
    clone: "json",
    acfe_json: "json",
    json: "json",
    google_map: "json",
  };
  return m[t] || "text";
}

function extractFieldNodes(fields, groupName, out) {
  const list = Array.isArray(fields) ? fields : [];
  for (const f of list) {
    if (!f || typeof f !== "object") continue;
    const slug = normalizeSlug(f.name || f.slug || f.key);
    const label = String(f.label || f.name || slug || "Field");
    if (slug) {
      out.push({
        slug,
        label,
        groupName,
        source: "acf_import",
        fieldType: mapAcfWidgetType(f.type),
        ...inferPanelScope(groupName),
      });
    }
    extractFieldNodes(f.sub_fields, groupName, out);
    if (Array.isArray(f.layouts)) {
      for (const l of f.layouts) {
        extractFieldNodes(l?.sub_fields, groupName, out);
      }
    }
  }
}

function parseAcfExport(json) {
  const items = Array.isArray(json) ? json : Array.isArray(json?.acf_field_groups) ? json.acf_field_groups : [];
  const groups = [];
  const fields = [];
  for (const g of items) {
    if (!g || typeof g !== "object") continue;
    const name = String(g.title || g.label || g.name || "ACF Group").trim();
    const id = mkId("grp");
    const scope = inferPanelScope(name);
    groups.push({
      id,
      name,
      appliesToCommercial: scope.appliesToCommercial,
      appliesToExpansion: scope.appliesToExpansion,
    });
    const groupFields = [];
    extractFieldNodes(g.fields, name, groupFields);
    for (const f of groupFields) {
      fields.push({
        id: mkId("fld"),
        slug: f.slug,
        label: f.label,
        groupName: name,
        source: f.source,
        appliesToCommercial: f.appliesToCommercial,
        appliesToExpansion: f.appliesToExpansion,
        description: "",
        fieldType: f.fieldType || "text",
        acfGroupName: "",
        fieldPath: "",
      });
    }
  }
  return { groups, fields };
}

function normalizeModel(raw) {
  const groups = Array.isArray(raw?.groups) ? raw.groups : [];
  const fields = Array.isArray(raw?.fields) ? raw.fields : [];
  return {
    groups: groups.map((g) => ({
      id: String(g.id || mkId("grp")),
      name: String(g.name || "Untitled Group"),
      domainKey: g.domainKey ? String(g.domainKey) : undefined,
      appliesToCommercial: g.appliesToCommercial !== false,
      appliesToExpansion: g.appliesToExpansion !== false,
    })),
    fields: fields
      .map((f) => ({
        id: String(f.id || mkId("fld")),
        slug: normalizeSlug(f.slug),
        label: String(f.label || f.slug || "Field"),
        groupId: f.groupId ? String(f.groupId) : null,
        source: String(f.source || "manual"),
        appliesToCommercial: f.appliesToCommercial !== false,
        appliesToExpansion: f.appliesToExpansion !== false,
        description: String(f.description || ""),
        fieldType: normalizeCatalogFieldType(f.fieldType),
        acfGroupName: String(f.acfGroupName || "").trim(),
        fieldPath: String(f.fieldPath || "").trim(),
      }))
      .filter((f) => f.slug),
  };
}

function sortFieldsList(a, b) {
  const ga = (a.acfGroupName || "").toLowerCase();
  const gb = (b.acfGroupName || "").toLowerCase();
  if (ga !== gb) return ga.localeCompare(gb);
  const pa = (a.fieldPath || a.slug || "").toLowerCase();
  const pb = (b.fieldPath || b.slug || "").toLowerCase();
  if (pa !== pb) return pa.localeCompare(pb);
  return a.slug.localeCompare(b.slug);
}

function resolveCatalogGroupId(groups, acfGroupName, slug = "") {
  return resolveFieldCatalogGroupId(groups, acfGroupName, slug);
}

function emptyFieldRow(groupId = null) {
  return {
    id: mkId("fld"),
    slug: "new_field_slug",
    label: "New Field",
    groupId,
    source: "manual",
    appliesToCommercial: true,
    appliesToExpansion: false,
    description: "",
    fieldType: "text",
    acfGroupName: "",
    fieldPath: "",
  };
}

function FieldRow({ f, model, setModel, normalizeSlug }) {
  return (
    <tr>
      <td>
        <input
          className="email-triggers-input"
          value={f.slug}
          onChange={(e) => {
            const fields = model.fields.map((x) =>
              x.id === f.id ? { ...x, slug: normalizeSlug(e.target.value) } : x
            );
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <input
          className="email-triggers-input"
          value={f.label}
          onChange={(e) => {
            const fields = model.fields.map((x) => (x.id === f.id ? { ...x, label: e.target.value } : x));
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <select
          className="user-matrix-select"
          value={f.fieldType || "text"}
          onChange={(e) => {
            const fields = model.fields.map((x) =>
              x.id === f.id ? { ...x, fieldType: normalizeCatalogFieldType(e.target.value) } : x
            );
            setModel({ ...model, fields });
          }}
        >
          {CATALOG_FIELD_TYPES.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="email-triggers-input"
          value={f.acfGroupName || ""}
          placeholder="JSON group"
          title="ACF / JSON group key (where the value lives)"
          onChange={(e) => {
            const fields = model.fields.map((x) => (x.id === f.id ? { ...x, acfGroupName: e.target.value } : x));
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <input
          className="email-triggers-input"
          value={f.fieldPath || ""}
          placeholder="path.in.json"
          title="Path under that group"
          onChange={(e) => {
            const fields = model.fields.map((x) => (x.id === f.id ? { ...x, fieldPath: e.target.value } : x));
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <select
          className="user-matrix-select"
          value={f.groupId || ""}
          onChange={(e) => {
            const fields = model.fields.map((x) =>
              x.id === f.id ? { ...x, groupId: e.target.value || null } : x
            );
            setModel({ ...model, fields });
          }}
        >
          <option value="">Unassigned</option>
          {model.groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          type="checkbox"
          checked={f.appliesToCommercial !== false}
          onChange={(e) => {
            const fields = model.fields.map((x) =>
              x.id === f.id ? { ...x, appliesToCommercial: e.target.checked } : x
            );
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <input
          type="checkbox"
          checked={f.appliesToExpansion !== false}
          onChange={(e) => {
            const fields = model.fields.map((x) =>
              x.id === f.id ? { ...x, appliesToExpansion: e.target.checked } : x
            );
            setModel({ ...model, fields });
          }}
        />
      </td>
      <td>
        <button
          type="button"
          className="email-triggers-remove"
          onClick={() => setModel({ ...model, fields: model.fields.filter((x) => x.id !== f.id) })}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

function FieldsTable({ fields, model, setModel, normalizeSlug }) {
  if (fields.length === 0) {
    return <p className="pb-field-catalog-empty email-triggers-muted">No fields in this group yet.</p>;
  }
  return (
    <div className="email-triggers-table-wrap">
      <table className="email-triggers-table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Label</th>
            <th>Type</th>
            <th>ACF / JSON group</th>
            <th>Path</th>
            <th>Move to</th>
            <th>Comm.</th>
            <th>Exp.</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <FieldRow key={f.id} f={f} model={model} setModel={setModel} normalizeSlug={normalizeSlug} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FieldGroupingPluginPanel() {
  const [model, setModel] = useState({ groups: [], fields: [] });
  const [loading, setLoading] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const sortedFields = useMemo(() => [...model.fields].sort(sortFieldsList), [model.fields]);
  const { byGroupId, unassigned } = useMemo(() => {
    const validIds = new Set(model.groups.map((g) => g.id));
    const map = new Map(model.groups.map((g) => [g.id, []]));
    const un = [];
    for (const f of sortedFields) {
      const gid = f.groupId;
      if (gid && validIds.has(gid)) map.get(gid).push(f);
      else un.push(f);
    }
    return { byGroupId: map, unassigned: un };
  }, [model.groups, sortedFields]);

  const groupByName = useMemo(() => {
    const map = new Map();
    for (const g of model.groups) map.set(g.name.toLowerCase(), g.id);
    return map;
  }, [model.groups]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const cfg = await fetchAppConfig();
      const next = normalizeModel(cfg?.fieldGrouping || {});
      const hasCommercialGroups = COMMERCIAL_DOMAIN_LIST.every((domainKey) =>
        next.groups.some((g) => g.domainKey === domainKey || g.id === stableCommercialGroupId(domainKey))
      );
      const needsAlign = next.groups.length === 0 || !hasCommercialGroups;
      const hydrated = needsAlign
        ? applyCommercialFieldCatalogStructure(next, { mkFieldId: () => mkId("fld") })
        : next;
      setModel(hydrated);
      setStatus(
        needsAlign
          ? "Aligned workflow groups with commercial project domains. Save to persist."
          : "Loaded field grouping settings."
      );
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not load field grouping settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (next) => {
    setError("");
    setStatus("");
    const normalized = normalizeModel(next);
    setModel(normalized);
    try {
      await putAdminAppConfig({ fieldGrouping: normalized });
      setStatus("Saved.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not save field grouping settings.");
    }
  }, []);

  const addSuggestedWorkflowGroups = useCallback(() => {
    const next = applyCommercialFieldCatalogStructure(model, { mkFieldId: () => mkId("fld") });
    setModel(next);
    setStatus(
      "Applied commercial project structure (Project summary → Engineering → Construction → Fiber → Networking → Accounting). Review fields below, then Save."
    );
  }, [model]);

  const applyCommercialStructure = useCallback(async () => {
    const next = applyCommercialFieldCatalogStructure(model, { mkFieldId: () => mkId("fld") });
    await save(next);
    setStatus("Saved commercial field groups and schema fields.");
  }, [model, save]);

  const mergeFromBoardProjects = useCallback(async () => {
    setScanBusy(true);
    setError("");
    setStatus("");
    try {
      const rows = await fetchAllProjectRowsForCatalog();
      const { entries } = scanFieldCatalogFromProjectRows(rows);
      const bySlug = new Map(model.fields.map((f) => [f.slug, { ...f }]));
      let added = 0;
      for (const e of entries) {
        const gid = resolveCatalogGroupId(model.groups, e.acfGroupName, e.slug);
        if (!bySlug.has(e.slug)) {
          bySlug.set(e.slug, {
            id: mkId("fld"),
            slug: e.slug,
            label: e.label,
            groupId: gid,
            source: "board_scan",
            appliesToCommercial: true,
            appliesToExpansion: true,
            description: "",
            fieldType: e.fieldType,
            acfGroupName: e.acfGroupName,
            fieldPath: e.fieldPath,
          });
          added += 1;
        } else {
          const prev = bySlug.get(e.slug);
          bySlug.set(e.slug, {
            ...prev,
            fieldType:
              prev.fieldType && prev.fieldType !== "text" ? prev.fieldType : normalizeCatalogFieldType(e.fieldType),
            acfGroupName: prev.acfGroupName || e.acfGroupName,
            fieldPath: prev.fieldPath || e.fieldPath,
            label: prev.label && prev.label !== prev.slug ? prev.label : e.label,
            groupId: prev.groupId || gid || null,
          });
        }
      }
      const nextFields = [...bySlug.values()].sort(sortFieldsList);
      setModel({ ...model, fields: nextFields });
      setStatus(
        `Merged ${entries.length} field paths from ${rows.length} projects (${added} new). Fields are grouped below; adjust “Move to” then Save.`
      );
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not load projects for scan.");
    } finally {
      setScanBusy(false);
    }
  }, [model]);

  const seedFromCurrentColumns = useCallback(() => {
    const descriptors = getProjectGridColumnDescriptors();
    const existing = new Map(model.fields.map((f) => [f.slug, f]));
    const seeded = descriptors.map((d) => {
      const slug = normalizeSlug(d.colId);
      const gid = inferFieldCatalogGroupId(model.groups, { slug }) || null;
      return (
        existing.get(slug) || {
          id: mkId("fld"),
          slug,
          label: d.headerName || slug,
          groupId: gid,
          source: "seed_grid",
          appliesToCommercial: true,
          appliesToExpansion: true,
          description: "",
          fieldType: "text",
          acfGroupName: "",
          fieldPath: "",
        }
      );
    });
    void save({ ...model, fields: seeded });
  }, [model, save]);

  const importAcf = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      try {
        const txt = await file.text();
        const parsed = JSON.parse(txt);
        const imported = parseAcfExport(parsed);
        if (imported.fields.length === 0) {
          setError("No fields found in this ACF export file.");
          return;
        }
        const groups = [...model.groups];
        const groupIdByName = new Map(groupByName);
        for (const g of imported.groups) {
          const key = g.name.toLowerCase();
          if (groupIdByName.has(key)) continue;
          const id = mkId("grp");
          groupIdByName.set(key, id);
          groups.push({ ...g, id });
        }
        const bySlug = new Map(model.fields.map((f) => [f.slug, f]));
        for (const f of imported.fields) {
          const gidExact = groupIdByName.get(String(f.groupName || "").toLowerCase()) || null;
          const gid =
            gidExact ||
            resolveCatalogGroupId(groups, f.groupName, f.slug) ||
            inferFieldCatalogGroupId(groups, { acfGroupName: f.groupName, slug: f.slug });
          const prev = bySlug.get(f.slug);
          bySlug.set(f.slug, {
            id: prev?.id || mkId("fld"),
            slug: f.slug,
            label: f.label || prev?.label || f.slug,
            groupId: gid || prev?.groupId || null,
            source: "acf_import",
            appliesToCommercial: f.appliesToCommercial,
            appliesToExpansion: f.appliesToExpansion,
            description: prev?.description || "",
            fieldType: normalizeCatalogFieldType(prev?.fieldType || f.fieldType),
            acfGroupName: prev?.acfGroupName || String(f.groupName || "").trim(),
            fieldPath: prev?.fieldPath || "",
          });
        }
        await save({ groups, fields: [...bySlug.values()] });
        setStatus(`Imported ${imported.fields.length} fields from ACF export.`);
      } catch (err) {
        setError(err?.message || "Could not parse ACF JSON export.");
      }
    },
    [groupByName, model.fields, model.groups, save]
  );

  const totalFieldCount = model.fields.length;

  return (
    <section className="plugins-card plugins-card-wide">
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        Tables — Field catalog
      </h3>
      <div className="plugins-card-desc pb-field-catalog-intro" style={PB.intro}>
        <p style={{ marginTop: 0 }}>
          Workflow groups mirror commercial project domains:{" "}
          <strong>Project summary</strong>, <strong>Engineering</strong>, <strong>Construction</strong>,{" "}
          <strong>Fiber</strong>, <strong>Networking</strong>, and <strong>Accounting</strong>. Assign scanned or
          imported fields with <strong>Move to</strong>; unassigned fields sit at the bottom.
        </p>
      </div>
      <div className="user-matrix-invite-row">
        <button type="button" className="email-triggers-button" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Reload from server"}
        </button>
        <button type="button" className="email-triggers-button" onClick={mergeFromBoardProjects} disabled={scanBusy}>
          {scanBusy ? "Scanning…" : "Merge fields from all projects"}
        </button>
        <button type="button" className="email-triggers-button" onClick={seedFromCurrentColumns}>
          Seed from grid columns
        </button>
        <label className="email-triggers-button email-triggers-button-primary" style={{ display: "inline-block" }}>
          Import ACF JSON
          <input
            type="file"
            accept="application/json,.json"
            onChange={importAcf}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          />
        </label>
      </div>
      {status ? <p className="email-triggers-muted">{status}</p> : null}
      {error ? <p className="email-triggers-muted">{error}</p> : null}

      <div className="email-triggers-section">
        <h4 className="email-triggers-subheading">Workflow groups</h4>
        <p className="email-triggers-muted" style={{ marginTop: "-0.25rem" }}>
          These six groups match the commercial landing page tabs and <code>acf_groups_json</code> domains (
          <code>project_summary</code>, <code>engineering_details</code>, etc.). Use{" "}
          <strong>Apply commercial structure</strong> to reset groups and seed schema fields.
        </p>
        <div className="email-triggers-table-wrap">
          <table className="email-triggers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Commercial</th>
                <th>Expansion</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {model.groups.map((g) => (
                <tr key={g.id}>
                  <td>
                    <input
                      className="email-triggers-input"
                      value={g.name}
                      onChange={(e) => {
                        const groups = model.groups.map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x));
                        setModel({ ...model, groups });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={g.appliesToCommercial !== false}
                      onChange={(e) => {
                        const groups = model.groups.map((x) =>
                          x.id === g.id ? { ...x, appliesToCommercial: e.target.checked } : x
                        );
                        setModel({ ...model, groups });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={g.appliesToExpansion !== false}
                      onChange={(e) => {
                        const groups = model.groups.map((x) =>
                          x.id === g.id ? { ...x, appliesToExpansion: e.target.checked } : x
                        );
                        setModel({ ...model, groups });
                      }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="email-triggers-remove"
                      onClick={() => {
                        const groups = model.groups.filter((x) => x.id !== g.id);
                        const fields = model.fields.map((f) => (f.groupId === g.id ? { ...f, groupId: null } : f));
                        setModel({ groups, fields });
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="user-matrix-invite-row" style={{ marginTop: "0.5rem" }}>
          <button
            type="button"
            className="email-triggers-button"
            onClick={() =>
              setModel({
                ...model,
                groups: [
                  ...model.groups,
                  { id: mkId("grp"), name: "New Group", appliesToCommercial: true, appliesToExpansion: true },
                ],
              })
            }
          >
            Add group
          </button>
          <button type="button" className="email-triggers-button" onClick={addSuggestedWorkflowGroups}>
            Apply commercial structure (preview)
          </button>
          <button
            type="button"
            className="email-triggers-button email-triggers-button-primary"
            onClick={() => void applyCommercialStructure()}
          >
            Apply commercial structure &amp; save
          </button>
        </div>
      </div>

      <div className="email-triggers-section">
        <h4 className="email-triggers-subheading">Fields by group ({totalFieldCount})</h4>
        <div className="pb-field-catalog-fields-root" style={PB.fieldsRoot}>
          {model.groups.map((g) => {
            const list = byGroupId.get(g.id) || [];
            return (
              <div key={g.id} className="pb-field-catalog-group-block" style={PB.groupBlock(false)}>
                <div className="pb-field-catalog-group-head" style={PB.groupHead}>
                  <div className="pb-field-catalog-group-head-main">
                    <h5 className="pb-field-catalog-group-title" style={PB.groupTitle}>
                      {g.name}
                    </h5>
                    <div className="pb-field-catalog-group-meta" style={PB.groupMeta}>
                      <span style={PB.pill("c")}>Commercial{g.appliesToCommercial !== false ? " on" : " off"}</span>
                      <span style={PB.pill("e")}>Expansion{g.appliesToExpansion !== false ? " on" : " off"}</span>
                      <span style={PB.pill("n")}>{list.length} field{list.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <div className="pb-field-catalog-group-actions" style={PB.groupActions}>
                    <button
                      type="button"
                      className="email-triggers-button"
                      onClick={() => setModel({ ...model, fields: [...model.fields, emptyFieldRow(g.id)] })}
                    >
                      Add field here
                    </button>
                  </div>
                </div>
                <FieldsTable fields={list} model={model} setModel={setModel} normalizeSlug={normalizeSlug} />
              </div>
            );
          })}

          <div className="pb-field-catalog-group-block pb-field-catalog-group-block--muted" style={PB.groupBlock(true)}>
            <div className="pb-field-catalog-group-head" style={PB.groupHead}>
              <div className="pb-field-catalog-group-head-main">
                <h5 className="pb-field-catalog-group-title" style={PB.groupTitle}>
                  Unassigned
                </h5>
                <div className="pb-field-catalog-group-meta" style={PB.groupMeta}>
                  <span style={PB.pill("n")}>{unassigned.length} field{unassigned.length === 1 ? "" : "s"}</span>
                  <span>Fields not linked to a workflow group yet — use “Move to” to organize.</span>
                </div>
              </div>
              <div className="pb-field-catalog-group-actions" style={PB.groupActions}>
                <button
                  type="button"
                  className="email-triggers-button"
                  onClick={() => setModel({ ...model, fields: [...model.fields, emptyFieldRow(null)] })}
                >
                  Add field here
                </button>
              </div>
            </div>
            <FieldsTable fields={unassigned} model={model} setModel={setModel} normalizeSlug={normalizeSlug} />
          </div>
        </div>
        <div className="user-matrix-invite-row" style={{ marginTop: "0.75rem" }}>
          <button type="button" className="email-triggers-button" onClick={() => setModel({ ...model, fields: [...model.fields, emptyFieldRow(null)] })}>
            Add field (unassigned)
          </button>
        </div>
      </div>

      <div className="user-matrix-invite-row">
        <button type="button" className="email-triggers-button email-triggers-button-primary" onClick={() => save(model)}>
          Save field catalog
        </button>
      </div>
    </section>
  );
}
